import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
  effect,
  Renderer2,
  Inject,
  HostListener,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {select} from '@ngrx/store';
import {Store} from '@ngrx/store';
import {filter, map, Observable} from 'rxjs';
import {Router} from '@angular/router';
import {DOCUMENT} from '@angular/common';

import {MENU_ITEMS, VERSION} from 'src/app/app.constants';
import {User} from 'src/app/core/model/user';
import {selectActiveUserRole} from 'src/app/core/store/active-user/active-user.selector';
import {getMenuItemsByRole} from 'src/app/shared/directives/utils/get-role-permission-by-key';
import {MobileService} from 'src/app/core/page/mobile.service';
import {IsRouteActivePipe} from '../../shared/pipes/is-route-active.pipe';
import {MENU_NAV_ITEMS, MenuItemGroupKey, MenuNavItem, MenuNavItems, MenuItem} from './../../../../app.constants';
import {Role} from 'src/app/core/model/role';
import {MenuRoutesService} from '../../services/menu-routes.service';

@Component({
  selector: 'mobile-navbar',
  templateUrl: './mobile-navbar.component.html',
  styleUrls: ['./mobile-navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [IsRouteActivePipe],
})
export class MobileNavbarComponent {
  @Input() user: User;
  @Input() routerUrl: string;
  @Output() logout = new EventEmitter();

  private destroyRef = inject(DestroyRef);

  userRole$: Observable<Role> = this.store$.pipe(select(selectActiveUserRole));
  filteredMenuItems$: Observable<MenuItem[]>;
  version = VERSION;
  menuNavItems: MenuNavItems = MENU_NAV_ITEMS;
  visibleMenuNavItems = signal<MenuNavItems>(MENU_NAV_ITEMS);
  isMenuOpen = signal<boolean>(false);
  groupMenuItemSelected = signal<MenuItemGroupKey>(null);
  isRoutesChanging = signal<boolean>(false);

  // Observable that emits the filtered menu items for the selected group
  currentGroupRoutes$: Observable<MenuItem[]>;

  constructor(
    private store$: Store,
    private mobileService: MobileService,
    private router: Router,
    private isRouteActivePipe: IsRouteActivePipe,
    private menuRoutesService: MenuRoutesService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.filteredMenuItems$ = this.observeUserRole();
    this.currentGroupRoutes$ = this.menuRoutesService.getRoutesForSelectedGroup$(this.filteredMenuItems$, true);

    this.filteredMenuItems$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(filteredItems => {
      this.visibleMenuNavItems.set(MENU_NAV_ITEMS.filter(item => this.isNavItemVisible(item, filteredItems)));
    });

    // Effect to handle body scroll prevention when menu is open
    effect(() => {
      const isOpen = this.isMenuOpen();
      if (isOpen) {
        this.renderer.addClass(this.document.body, 'mobile-menu-open');
      } else {
        this.renderer.removeClass(this.document.body, 'mobile-menu-open');
      }
    });
  }

  observeUserRole(): Observable<MenuItem[]> {
    return this.userRole$.pipe(
      filter(role => !!role),
      map(role => {
        const roleDisplayName = role.displayName?.toLowerCase();
        const rolePermissions = role.permissions;
        const isMobileApp = this.mobileService.isMobilePlatform();

        let menuItems = MENU_ITEMS;

        // Filter based on role
        if (roleDisplayName !== 'admin') {
          menuItems = menuItems.filter(item => !item.adminOnly);
          menuItems = getMenuItemsByRole(rolePermissions, menuItems);
        }

        // Filter based on mobile app context
        if (!isMobileApp) {
          menuItems = menuItems.filter(item => !item.mobileAppOnly);
        }

        return menuItems;
      })
    );
  }

  isNavItemVisible(item: MenuNavItem, filteredItems: MenuItem[]): boolean {
    const menuItem = item as MenuItem;
    if (!menuItem.rolePermissionKey) return true;
    return filteredItems.some(fi => fi.url === menuItem.url);
  }

  closeGroupMenu(): void {
    this.isMenuOpen.set(false);
    this.groupMenuItemSelected.set(null);
    this.menuRoutesService.clearSelectedGroup();
  }

  itemClickHandler(item: MenuNavItem): void {
    const menuItem = item as MenuItem;
    if (!menuItem.url) return;
    this.router.navigate([menuItem.url]);
    this.closeGroupMenu();
  }

  groupClickHandler(item?: MenuNavItem): void {
    const itemGroupKey = item?.itemGroup;

    if (itemGroupKey) {
      // If the same group is selected, close the menu
      if (this.groupMenuItemSelected() === itemGroupKey) {
        this.closeGroupMenu();
      } else {
        // If a different group is selected, animate the transition
        if (this.isMenuOpen()) {
          // Trigger animation for route change
          this.isRoutesChanging.set(true);

          // Wait for fade out, then change routes
          setTimeout(() => {
            this.groupMenuItemSelected.set(itemGroupKey);
            this.menuRoutesService.setSelectedGroup(itemGroupKey);
            this.isRoutesChanging.set(false);
          }, 150); // Half of the transition duration
        } else {
          // If menu is closed, just open with new group
          this.groupMenuItemSelected.set(itemGroupKey);
          this.menuRoutesService.setSelectedGroup(itemGroupKey);
          this.isMenuOpen.set(true);
        }
      }
    } else {
      // Close the menu when no item is provided (overlay/close button click)
      this.closeGroupMenu();
    }
  }

  metaItemClickHandler(item: MenuNavItem): void {
    if (item?.meta?.isLogout) {
      this.logout.emit();
    }
    this.closeGroupMenu();
  }

  getActiveButtonPosition(): number {
    const visibleItems = this.visibleMenuNavItems();
    const activeIndex = visibleItems.findIndex(item => this.isRouteActivePipe.transform(item, this.routerUrl));
    if (activeIndex === -1) return 0;

    // Use percentage-based positioning for better alignment
    const totalButtons = visibleItems.length;
    // Calculate the center position of each button as a percentage
    // With space-around, buttons are evenly distributed
    const sectionWidth = 100 / totalButtons; // Each button gets equal percentage
    const buttonCenterPercentage = sectionWidth * activeIndex + sectionWidth / 2;
    // Adjust for the indicator width (convert 60px to percentage of 290px container)
    const sliderWidth = 60;
    const navbarWidth = 290;
    const indicatorWidthPercentage = (sliderWidth / navbarWidth) * 100;
    return buttonCenterPercentage - indicatorWidthPercentage / 2;
  }

  // Handle ESC key to close menu
  @HostListener('window:keydown.escape', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    if (this.isMenuOpen()) {
      event.preventDefault();
      this.isMenuOpen.set(false);
      this.groupMenuItemSelected.set(null);
      this.menuRoutesService.clearSelectedGroup();
    }
  }
}
